import { CheckoutResponseForState, CheckoutState, OriginString } from '@shared/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInitializeCheckout } from './useInitializeCheckout';
import { CademyError } from '@shared/domain-shared';
import * as Bugsnag from '../../../services/bugsnag';
import { useIam } from '../../../services/IAm/hooks';

export type ResponseSubmitter<TCheckoutState extends CheckoutState> = (
    action: CheckoutResponseForState<TCheckoutState>
) => Promise<void>;

export type UseCheckoutProps = {
    courseId: string;
    origin: OriginString;
    flow: CheckoutState['flow'];
    checkoutId?: string;
    occurrenceId?: string;
    onCheckoutIdChange?: (newCheckoutId: string) => void;
};

export type UseCheckoutResult =
    | {
          isReady: false;
          checkoutState: null;
          isLoading: true;
          submitResponse: null;
          error: CademyError | null;
      }
    | {
          isReady: true;
          checkoutState: CheckoutState;
          isLoading: boolean;
          submitResponse: ResponseSubmitter<CheckoutState>;
          error: CademyError | null;
      };

export const useCheckout = ({
    courseId,
    origin,
    flow,
    checkoutId,
    occurrenceId,
    onCheckoutIdChange,
}: UseCheckoutProps): UseCheckoutResult => {
    const {
        initialised,
        checkoutState: initialCheckoutState,
        error: initialiseError,
    } = useInitializeCheckout({
        courseId,
        origin,
        flow,
        initialCheckoutId: checkoutId,
        onCheckoutIdChange,
        occurrenceId,
    });
    const [checkoutState, setCheckoutState] = useState<CheckoutState | null>(initialCheckoutState);
    const [submitError, setSubmitError] = useState<CademyError | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { iam } = useIam();

    useEffect(() => {
        setCheckoutState(initialCheckoutState);
    }, [initialCheckoutState]);

    const isReady = useMemo(() => {
        return initialised === true;
    }, [initialised]);

    const error = useMemo(() => {
        return initialiseError || submitError;
    }, [initialiseError, submitError]);

    const submitResponse: ResponseSubmitter<CheckoutState> = useCallback(
        async (action) => {
            if (!checkoutState) {
                return;
            }
            setIsLoading(true);
            setSubmitError(null);
            try {
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/checkout/${checkoutState._id}/submit-response`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(action),
                        credentials: 'include',
                    }
                );
                if (response.ok === false) {
                    const responseBody = await response.text();
                    const contentType = (response.headers.get('content-type') || '').toLowerCase();

                    if (contentType.includes('application/problem+json')) {
                        const problemDetails = JSON.parse(responseBody);
                        const error = CademyError.fromObject(problemDetails);
                        throw error;
                    }
                    if (contentType.includes('application/json')) {
                        const errorJSON = JSON.parse(responseBody);
                        throw CademyError.fromUnknown(errorJSON);
                    }
                    throw new Error(responseBody);
                }
                setCheckoutState(await response.json());
            } catch (error) {
                console.error(error);
                const defaultError = new CademyError(
                    'checkout/submit-response',
                    'There was an issue submitting your response, check if there are any required fields that you have missed',
                    'If the problem persists, please contact support.',
                    400
                );
                setSubmitError(CademyError.fromUnknown(error, defaultError));
            }
            setIsLoading(false);
        },
        [checkoutState, setCheckoutState, iam]
    );

    if (!isReady || !checkoutState) {
        return {
            isReady: false,
            checkoutState: null,
            isLoading: true,
            submitResponse: null,
            error,
        };
    }

    return {
        isReady: true,
        checkoutState: checkoutState,
        isLoading,
        submitResponse,
        error,
    };
};
